/**
 * Interactive-copilot wire schemas (Zod).
 *
 * The `/ws/copilot` relay and this frontend agree on a §4 "house envelope" — a
 * `{ type, data }` frame. Two inbound (relay → FE) kinds, one per adopted
 * transport concern:
 *   - `agui.event` — carries an AG-UI protocol event plus the `{ runId, seq }`
 *     durability cursor (and, on RUN_STARTED, the resolved `threadId`).
 *   - `agui.error` — a relay-level failure (feature disabled, auth, busy,
 *     wall-clock timeout) as `{ code, message }`.
 *
 * Outbound (FE → relay) frames are `agui.run` (the opening operator turn),
 * `agui.client_tool_result` (a mid-run browser-executed tool's result),
 * `agui.attach` (the reconnect-resume opener that replaces `agui.run` on
 * reattach), and `agui.cancel` (cancel the in-flight run); those are constructed
 * in the service, not parsed, so they have no inbound schema.
 *
 * `AgentChatService` is the single `JSON.parse` boundary for the copilot socket;
 * it `safeParse`s EVERY inbound frame against {@link HouseEnvelopeSchema} and the
 * REST-repaint rows against {@link AguiEventSchema}. An invalid frame is logged
 * and dropped — never applied, never allowed to crash the transcript. Each event
 * member keeps `.passthrough()` so additive backend fields survive validation
 * (mirrors the platform's other WS schemas, e.g.
 * `performance-websocket-schemas.ts`), while the discriminant `type` is pinned to
 * the adopted AG-UI subset so an unknown/adversarial event type fails closed.
 */

import { boolean, discriminatedUnion, literal, number, object, string, type infer as Infer } from 'zod';

/**
 * Per-event AG-UI schemas — one per adopted event type. Composed into
 * {@link AguiEventSchema} as a discriminated union on `type`, so an event whose
 * `type` is outside this pinned set fails validation (and is dropped) rather than
 * being applied. Field-level requirements stay loose (all `.optional()`,
 * `.passthrough()`): the service's `applyEvent` already guards on each field, and
 * the win here is the discriminant gate, not strict payload shape.
 */
/**
 * Design-system §18 — RELAY-stamped evidence/provenance metadata. The relay
 * (the only trusted clock between browser and sidecar) stamps this onto
 * TOOL_CALL_RESULT (`latencyMs`, best-effort `rowCount`, `source`, `stampedAt`)
 * and RUN_FINISHED (`wallMs`) BEFORE persisting, so live frames, reconnect
 * replay, and the REST repaint all carry it. All fields optional: rows persisted
 * before the stamping deploy simply render without the ms/rows cells. The relay
 * strips any sidecar-supplied `razorMeta`, so a present stamp is always genuine.
 */
export const RazorMetaSchema = object({
    latencyMs: number().optional(),
    rowCount: number().optional(),
    source: string().optional(),
    stampedAt: string().optional(),
    wallMs: number().optional(),
    historyDegraded: boolean().optional(),
  })
  .passthrough();

export type RazorMeta = Infer<typeof RazorMetaSchema>;

export const RunStartedSchema = object({ type: literal('RUN_STARTED'), threadId: string().optional(), razorMeta: RazorMetaSchema.optional() })
  .passthrough();

export const RunFinishedSchema = object({
    type: literal('RUN_FINISHED'),
    result: object({
        tokensIn: number().optional(),
        tokensOut: number().optional(),
        toolCallCount: number().optional(),
      })
      .passthrough()
      .optional(),
    razorMeta: RazorMetaSchema.optional(),
  })
  .passthrough();

export const RunErrorSchema = object({
    type: literal('RUN_ERROR'),
    message: string().optional(),
    code: string().optional(),
  })
  .passthrough();

export const TextMessageStartSchema = object({ type: literal('TEXT_MESSAGE_START'), messageId: string().optional() })
  .passthrough();

export const TextMessageContentSchema = object({
    type: literal('TEXT_MESSAGE_CONTENT'),
    messageId: string().optional(),
    delta: string().optional(),
    // `true` on a WHOLE-MESSAGE snapshot: the relay's coalesced flush row (persisted
    // at a seq past every delta) and the in-flight re-attach replay. The FE REPLACES
    // the message's accumulated text on a marked event and APPENDS on an unmarked
    // live delta — so incremental replay, completed-message replay, and full-history
    // repaint all render the full text with no truncation or duplication.
    consolidated: boolean().optional(),
  })
  .passthrough();

export const TextMessageEndSchema = object({ type: literal('TEXT_MESSAGE_END'), messageId: string().optional() })
  .passthrough();

export const ToolCallStartSchema = object({
    type: literal('TOOL_CALL_START'),
    toolCallId: string().optional(),
    toolCallName: string().optional(),
  })
  .passthrough();

export const ToolCallArgsSchema = object({
    type: literal('TOOL_CALL_ARGS'),
    toolCallId: string().optional(),
    delta: string().optional(),
  })
  .passthrough();

export const ToolCallEndSchema = object({
    type: literal('TOOL_CALL_END'),
    toolCallId: string().optional(),
    // `true` on a REPLAYED TOOL_CALL_END: the relay stamps this on agui.event frames
    // it re-emits when a re-attaching consumer resumes a run (§6.4 F2·m1). It tells
    // the FE this terminal event is a transcript RECONSTRUCTION, not a live call —
    // so a side-effecting client tool must NOT re-execute in the browser and must NOT
    // re-send an agui.client_tool_result (the sidecar's pending future already timed
    // out during the detach window; a re-run would double-fire the action and the
    // result would be dropped). Mirrors the relay-only `consolidated` marker on
    // TEXT_MESSAGE_CONTENT. The REST repaint path signals the same via an explicit
    // `applyEvent(..., { replay:true })` opt, since persisted rows carry no marker.
    replay: boolean().optional(),
  })
  .passthrough();

export const ToolCallResultSchema = object({
    type: literal('TOOL_CALL_RESULT'),
    toolCallId: string().optional(),
    content: string().optional(),
    razorMeta: RazorMetaSchema.optional(),
  })
  .passthrough();

/**
 * The adopted inbound AG-UI event set. `CLIENT_TOOL_RESULT` is deliberately
 * absent — it is FE → relay only (never forwarded back to us), so a persisted
 * `CLIENT_TOOL_RESULT` row is skipped on repaint rather than re-rendered.
 */
export const AguiEventSchema = discriminatedUnion('type', [
  RunStartedSchema,
  RunFinishedSchema,
  RunErrorSchema,
  TextMessageStartSchema,
  TextMessageContentSchema,
  TextMessageEndSchema,
  ToolCallStartSchema,
  ToolCallArgsSchema,
  ToolCallEndSchema,
  ToolCallResultSchema,
]);

/** A validated inbound AG-UI event — the discriminated union narrows per `type`. */
export type AguiEvent = Infer<typeof AguiEventSchema>;

/**
 * `agui.event` — the streamed AG-UI event wrapped with its durability cursor.
 * `runId` + `seq` drive the reconnect splice; `threadId` (when present, echoed on
 * RUN_STARTED) lets follow-up turns continue the same conversation.
 */
export const AguiEventEnvelopeSchema = object({
    type: literal('agui.event'),
    data: object({
        runId: string(),
        seq: number(),
        threadId: string().optional(),
        event: AguiEventSchema,
      })
      .passthrough(),
  })
  .passthrough();

/**
 * `agui.error` — a relay-level failure surfaced to the operator. `message` is
 * optional: the re-attach rejections (`{code:"attach_failed"|"attach_forbidden"}`)
 * carry a machine code only, no human message, so the service keys on `code`.
 */
export const AguiErrorEnvelopeSchema = object({
    type: literal('agui.error'),
    data: object({ code: string(), message: string().optional() })
      .passthrough(),
  })
  .passthrough();

/** The §4 house envelope: every inbound copilot frame is one of these two. */
export const HouseEnvelopeSchema = discriminatedUnion('type', [
  AguiEventEnvelopeSchema,
  AguiErrorEnvelopeSchema,
]);

export type HouseEnvelope = Infer<typeof HouseEnvelopeSchema>;
export type AguiEventEnvelope = Infer<typeof AguiEventEnvelopeSchema>;
export type AguiErrorEnvelope = Infer<typeof AguiErrorEnvelopeSchema>;
